import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { logError, logInfo } from '../config_and_helpers/logger';

const LOG_CONTEXT = 's3_media_client';

/** @typedef {'quote'|'order_image'|'product_image'} S3DownloadResourceType */

/**
 * Build query params for GET s3-media/download-url/ (resource_type + object_id).
 *
 * @param {Object} params
 * @param {number|string} [params.quoteId] - Maps to resource_type=quote
 * @param {number|string} [params.productImageId] - Maps to resource_type=product_image
 * @param {number|string} [params.orderImageId] - Maps to resource_type=order_image (OrderImage pk)
 * @param {S3DownloadResourceType} [params.resourceType]
 * @param {number|string} [params.objectId]
 * @returns {{ resource_type?: string, object_id?: number }}
 */
export function buildS3DownloadUrlQuery(params = {}) {
  let resourceType = params.resourceType;
  let objectId = params.objectId;

  if (params.quoteId != null && params.quoteId !== '') {
    resourceType = 'quote';
    objectId = params.quoteId;
  } else if (params.productImageId != null && params.productImageId !== '') {
    resourceType = 'product_image';
    objectId = params.productImageId;
  } else if (params.orderImageId != null && params.orderImageId !== '') {
    resourceType = 'order_image';
    objectId = params.orderImageId;
  }

  if (!resourceType || objectId == null || objectId === '') {
    return {};
  }

  const id = Number(objectId);
  if (!Number.isFinite(id) || id <= 0) {
    return {};
  }

  return {
    resource_type: resourceType,
    object_id: id,
  };
}

/**
 * Fetch a fresh presigned GET URL for private bucket media.
 *
 * @async
 * @param {string} token
 * @param {Object} params - See {@link buildS3DownloadUrlQuery}
 * @returns {Promise<{ success: boolean, download_url?: string, messages?: string[] }>}
 */
export async function getS3DownloadUrl(token, params = {}) {
  const query = buildS3DownloadUrlQuery(params);
  if (Object.keys(query).length === 0) {
    return {
      success: false,
      messages: ['Cannot refresh download URL: missing resource identifier.'],
    };
  }

  try {
    logInfo(LOG_CONTEXT, 'getS3DownloadUrl start', {
      resourceType: query.resource_type,
      objectId: query.object_id,
    });

    const response = await axios.get(`${BACKEND_URL}s3-media/download-url/`, {
      params: query,
      headers: { Authorization: `Token ${token}` },
    });

    const downloadUrl = response.data?.download_url ?? response.data?.url ?? '';
    if (!downloadUrl) {
      return {
        success: false,
        messages: ['Download URL was not returned by the server.'],
      };
    }

    logInfo(LOG_CONTEXT, 'getS3DownloadUrl success', {
      resourceType: query.resource_type,
      objectId: query.object_id,
    });
    return { success: true, download_url: downloadUrl };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT, 'getS3DownloadUrl failed', {
      status: error.response?.status,
      resourceType: query.resource_type,
      objectId: query.object_id,
    });
    const messages = formatS3DownloadUrlErrors(errPayload);
    return { success: false, messages, error: errPayload };
  }
}

/**
 * @param {Object|Array|string|null} errPayload
 * @returns {string[]}
 */
export function formatS3DownloadUrlErrors(errPayload) {
  if (errPayload == null) {
    return ['Could not refresh download link. Please try again.'];
  }
  if (typeof errPayload === 'string') {
    return [errPayload];
  }
  if (Array.isArray(errPayload)) {
    return errPayload.map(v => String(v));
  }
  const messages = [];
  if (Array.isArray(errPayload.non_field_errors)) {
    messages.push(...errPayload.non_field_errors.map(v => String(v)));
  }
  Object.keys(errPayload).forEach(key => {
    if (key === 'non_field_errors') return;
    const val = errPayload[key];
    if (Array.isArray(val)) {
      val.forEach(v => messages.push(String(v)));
    } else if (typeof val === 'string') {
      messages.push(val);
    }
  });
  return messages.length
    ? messages
    : ['Could not refresh download link. Please try again.'];
}
